<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();

const passwordStrong = helpers.withMessage(
  t('AGENT_MGMT.ADD.FORM.PASSWORD.REQUIREMENTS'),
  value => {
    if (!value) return true;
    return (
      /[A-Z]/.test(value) &&
      /[a-z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[!@#$%^&*()_+\-=[\]{}|\\":.,/<>;`?~']/.test(value)
    );
  }
);

const store = useStore();

const agentName = ref('');
const agentEmail = ref('');
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const selectedRoleId = ref('agent');
const creationMethod = ref('email');

const passwordRequired = computed(() => creationMethod.value === 'password');

const rules = computed(() => ({
  agentName: { required },
  agentEmail: { required, email },
  agentPassword: passwordRequired.value ? { required, passwordStrong } : {},
  agentPasswordConfirmation: passwordRequired.value ? { required } : {},
  selectedRoleId: { required },
}));

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  agentPassword,
  agentPasswordConfirmation,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  // Validar que las contraseñas coincidan si se eligió método de contraseña
  if (
    creationMethod.value === 'password' &&
    agentPassword.value !== agentPasswordConfirmation.value
  ) {
    useAlert(t('AGENT_MGMT.ADD.API.PASSWORD_MISMATCH'));
    return;
  }

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    // Solo agregar contraseña si se eligió ese método
    if (creationMethod.value === 'password') {
      payload.password = agentPassword.value;
      payload.password_confirmation = agentPasswordConfirmation.value;
    }

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <!-- Método de creación -->
      <div class="w-full">
        <label>
          {{ $t('AGENT_MGMT.ADD.FORM.CREATION_METHOD.LABEL') }}
        </label>
        <div
          class="flex flex-col gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
        >
          <p class="text-sm text-slate-600 dark:text-slate-400">
            {{ $t('AGENT_MGMT.ADD.FORM.CREATION_METHOD.DESCRIPTION') }}
          </p>

          <div class="flex flex-col gap-2">
            <div
              class="flex items-center gap-2 cursor-pointer"
              @click.stop="creationMethod = 'email'"
            >
              <input
                v-model="creationMethod"
                type="radio"
                value="email"
                class="w-4 h-4"
                @click.stop
              />
              <div class="flex flex-col">
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ $t('AGENT_MGMT.ADD.FORM.CREATION_METHOD.EMAIL.TITLE') }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t('AGENT_MGMT.ADD.FORM.CREATION_METHOD.EMAIL.DESCRIPTION')
                  }}
                </span>
              </div>
            </div>

            <div
              class="flex items-center gap-2 cursor-pointer"
              @click.stop="creationMethod = 'password'"
            >
              <input
                v-model="creationMethod"
                type="radio"
                value="password"
                class="w-4 h-4"
                @click.stop
              />
              <div class="flex flex-col">
                <span class="font-medium text-slate-900 dark:text-slate-100">
                  {{ $t('AGENT_MGMT.ADD.FORM.CREATION_METHOD.PASSWORD.TITLE') }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t(
                      'AGENT_MGMT.ADD.FORM.CREATION_METHOD.PASSWORD.DESCRIPTION'
                    )
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Campos de contraseña (solo si se selecciona método de contraseña) -->
      <div v-if="creationMethod === 'password'" class="w-full">
        <label :class="{ error: v$.agentPassword.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.LABEL') }}
          <input
            v-model="agentPassword"
            type="password"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.PASSWORD.PLACEHOLDER')"
            @input="v$.agentPassword.$touch"
          />
        </label>
        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD.REQUIREMENTS') }}
        </p>
        <span v-if="v$.agentPassword.$error" class="message">
          {{ v$.agentPassword.$errors[0].$message }}
        </span>
      </div>

      <div v-if="creationMethod === 'password'" class="w-full">
        <label :class="{ error: v$.agentPasswordConfirmation.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.LABEL') }}
          <input
            v-model="agentPasswordConfirmation"
            type="password"
            :placeholder="
              $t('AGENT_MGMT.ADD.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
            "
            @input="v$.agentPasswordConfirmation.$touch"
          />
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
